// @ts-check
/** @typedef {import('../types').Adapter<unknown, unknown>} Adapter */
/** @typedef {import('../types').Card} Card */
/** @typedef {import('../types').Plan} Plan */
/** @typedef {import('../types').CurrentPlan} CurrentPlan */
/** @typedef {import('../types').PlanHistory} PlanHistory */
/** @typedef {import('../types').PaymentHistory} PaymentHistory */
/** @typedef {import('./types.personal').ServerBillingInfo} ServerBillingInfo */
/** @typedef {import('./types.personal').ServerPlan} ServerPlan */
/** @typedef {import('./types.personal').ServerCurrentPlan} ServerCurrentPlan */
/** @typedef {import('./types.personal').ServerPlanHistory} ServerPlanHistory */
/** @typedef {import('./types.personal').ServerPaymentHistory} ServerPaymentHistory */

import { toDate } from '@/libs/date.js';
import { formatInt } from '@/libs/utils.js';
import { identity } from '../utils.js';

/** @type {Adapter} */
export const deserialize = {
    getCurrentPlan: identity,

    /**
     * @param {ServerPlan[]} raw
     * @returns {Plan[]}
     */
    getPlans: (raw) =>
        raw.map((r) => ({
            id: r.id,
            name: r.name,
            price: formatInt(r.price),
            limit: formatInt(r.limit),
            groupBEnabled: r.is_all_exchanger_supported,
            defiEnabled: r.is_defi_or_nft_supported,
            isCorporateOldPlan: r.is_corporate_old_plan,
        })),

    /**
     * @param {ServerBillingInfo} raw
     * @returns {Card | undefined}
     */
    getBillingInfo: (raw) => {
        if (raw.first_billing) return undefined;
        const card = raw.credit_card;
        return {
            id: card.id,
            last4: card.last4,
            expirationDate: `${card.exp_month}/${card.exp_year}`,
        };
    },

    /**
     * @param {ServerPlanHistory} raw
     * @returns {PlanHistory[]}
     */
    getPlanHistory: (raw) => {
        return Object.entries(raw).map(([key, value]) => ({ year: Number(key), count: value }));
    },

    /**
     * @param {string} raw
     * @returns {string}
     */
    getUpgradeCost: formatInt,

    subscribe: identity,

    upgradePlan: identity,

    enableAutoRenewal: identity,

    cancelAutoRenewal: identity,

    downgradePlan: identity,

    cancelDowngrade: identity,

    afterThreeDomainSecure: identity,

    afterUpgradeThreeDomainSecure: identity,

    completeRetry: identity,

    /**
     * @param {ServerPaymentHistory[]} raw
     * @returns {PaymentHistory[]}
     */
    getPaymentHistory: (raw) => {
        /** @type {Map<string, ServerPaymentHistory>} */
        const note = new Map();

        raw.forEach((payment) => {
            const noted = note.get(payment.invoice_id);
            if (!noted || noted.intended_at < payment.intended_at) {
                note.set(payment.invoice_id, payment);
            }
        });

        const latestPaymentsByPaymentId = Array.from(note.values()).map(mapPaymentHistory);
        latestPaymentsByPaymentId.sort((a, b) => toDate(b.date).getTime() - toDate(a.date).getTime());
        const filteredPayments = latestPaymentsByPaymentId.filter((payment, index) => {
            // 最新の支払いはアップグレードの失敗以外の履歴を表示
            if (index === 0) {
                return payment.success || !payment.isUpgradeAttempt;
            }
            // 2つ目以降は成功履歴のみ表示
            return payment.success;
        });

        filteredPayments[0].canRetry = filteredPayments[0]?.success === false;

        return filteredPayments;
    },

    /**
     * @param {{ billing_history: any }} raw
     * @returns {any}
     */
    getBillingHistory: (raw) => raw.billing_history,

    retryPayment: identity,

    changeTrialPlan: identity,
};

const mapPaymentHistory = (raw) => ({
    id: raw.id,
    invoiceId: raw.invoice_id,
    date: raw.intended_at,
    price: raw.amount,
    planId: raw.plan_name,
    couponName: raw.coupon_name,
    success: !!raw.is_success,
    isUpgradeAttempt: !!raw.upgrade_attempt,
});
